import React, { useState, useRef } from "react";
import { Breadcrumb, Card, Form, Button, Row, Col, Badge, Alert } from "react-bootstrap";
import { useAuth } from "../context/AuthProvider";
import { updateUser } from "../services/userService";
import { logStudentActivity } from "../services/activityService";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { toast } from "react-toastify";
import { User, Download, Upload, Save, Edit3 } from "lucide-react";

export default function Profile() {
  const { user, login } = useAuth();
  const dashboardLabel =
    user?.role === "student"
      ? "Student Dashboard"
      : user?.role === "tutor"
      ? "Tutor Dashboard"
      : "Dashboard";
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    avatarUrl: user?.avatarUrl || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);
  const resumeInputRef = useRef(null);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const avatarFileName = `avatars/${user.id}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, avatarFileName);
      await uploadBytes(storageRef, file);
      const avatarUrl = await getDownloadURL(storageRef);

      // Update user with new avatar URL
      const updatedUser = { ...user, avatarUrl };
      await updateUser(user.id, updatedUser);
      login(updatedUser);

      // Log activity
      const activity = {
        userId: user.id,
        action: "update_avatar",
        entityType: "user",
        entityId: user.id,
        description: `${user.name} updated profile picture`,
        metadata: {
          fileName: file.name,
          fileSize: file.size,
        },
      };

      logStudentActivity(activity).catch((err) => console.error(err));

      setForm((prev) => ({ ...prev, avatarUrl }));
      toast.success("Avatar uploaded successfully");
    } catch (error) {
      console.error("Avatar upload failed:", error);
      toast.error("Failed to upload avatar");
    }
    setUploading(false);
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf" && !file.name.endsWith(".pdf")) {
      toast.error("Please select a PDF file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Resume size should be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const resumeFileName = `resumes/${user.id}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, resumeFileName);
      await uploadBytes(storageRef, file);
      const resumeUrl = await getDownloadURL(storageRef);

      // Update user with new resume URL
      const updatedUser = { ...user, resumeUrl };
      await updateUser(user.id, updatedUser);
      login(updatedUser);

      // Log activity
      const activity = {
        userId: user.id,
        action: "update_resume",
        entityType: "user",
        entityId: user.id,
        description: `${user.name} updated resume`,
        metadata: {
          fileName: file.name,
          fileSize: file.size,
        },
      };

      logStudentActivity(activity).catch((err) => console.error(err));

      toast.success("Resume uploaded successfully");
    } catch (error) {
      console.error("Resume upload failed:", error);
      toast.error("Failed to upload resume");
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.warn("Name is required");

    setSaving(true);
    try {
      const payload = {
        ...user,
        name: form.name,
        phone: form.phone,
        avatarUrl: form.avatarUrl,
      };

      await updateUser(user.id, payload);
      login(payload);

      // Log activity
      const activity = {
        userId: user.id,
        action: "update_profile",
        entityType: "user",
        entityId: user.id,
        description: `${user.name} updated profile information`,
        metadata: {
          updatedFields: {
            name: form.name !== user.name,
            phone: form.phone !== user.phone,
          },
        },
      };

      logStudentActivity(activity).catch((err) => console.error(err));

      setIsEditing(false);
      toast.success("Profile updated successfully");
    } catch (err) {
      console.error("Failed to update profile:", err);
      toast.error("Failed to update profile");
    }
    setSaving(false);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case "employee":
        return "success";
      case "paid-trainee":
        return "info";
      case "free-trainee":
        return "secondary";
      default:
        return "dark";
    }
  };

  return (
    <div className="container-fluid py-4">
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item href="/dashboard">{dashboardLabel}</Breadcrumb.Item>
        <Breadcrumb.Item active>Profile</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        {/* Left Sidebar - Profile Info */}
        <Col lg={3} className="mb-4">
          <Card className="shadow-sm">
            <Card.Body className="text-center">
              <div className="position-relative d-inline-block mb-3">
                <div
                  className="rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center border border-gray-900"
                  style={{ width: "120px", height: "120px", margin: "0 auto" }}
                >
                  {form.avatarUrl ? (
                    <img
                      src={form.avatarUrl}
                      alt="Profile"
                      className="w-100 h-100 object-fit-cover"
                    />
                  ) : (
                    <User size={60} className="text-muted" />
                  )}
                </div>
                {isEditing && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="position-absolute bottom-0 end-0 rounded-circle"
                    style={{ width: "30px", height: "30px" }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload size={14} />
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  style={{ display: "none" }}
                />
              </div>

              <h4 className="mb-2">{user?.name}</h4>
              <p className="text-muted mb-3">{user?.email}</p>

              {user?.phone && (
                <div className="mb-3">
                  <small className="text-muted">Phone</small>
                  <div>{user.phone}</div>
                </div>
              )}

              {user?.studentLevel && (
                <Badge
                  bg={getLevelColor(user?.studentLevel)}
                  className="mb-3 px-3 py-2"
                >
                  {user?.studentLevel?.toUpperCase()}
                </Badge>
              )}

              {user?.enrollmentDate && (
                <div className="mb-3">
                  <small className="text-muted">Enrolled On</small>
                  <div>
                    {new Date(user.enrollmentDate).toLocaleDateString()}
                  </div>
                </div>
              )}

              {/* Resume Section */}
              <div className="mt-4 pt-3 border-top flex flex-column align-items-center">
                {user?.resumeUrl || isEditing ? (
                  <h6 className="mb-3">Resume</h6>
                ) : null}
                {user?.resumeUrl ? (
                  <div className="d-flex flex-column gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => window.open(user.resumeUrl, "_blank")}
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <Download size={16} className="me-1" />
                      <span>Download Resume</span>
                    </Button>
                    {isEditing && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => resumeInputRef.current?.click()}
                        disabled={uploading}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Upload size={16} className="me-1" />
                        <span>Update Resume</span>
                      </Button>
                    )}
                  </div>
                ) : (
                  isEditing && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => resumeInputRef.current?.click()}
                      disabled={uploading}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <Upload size={16} className="me-1" />
                      <span>Upload Resume</span>
                    </Button>
                  )
                )}
                <input
                  ref={resumeInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleResumeUpload}
                  style={{ display: "none" }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Content - Editable Details */}
        <Col lg={9}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Profile Details</h5>
              {!isEditing ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  style={{ display: "flex", alignItems: "center", gap: 4 }}
                >
                  <Edit3 size={16} className="me-1" />
                  <span>Edit Profile</span>
                </Button>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setForm({
                        name: user?.name || "",
                        phone: user?.phone || "",
                        avatarUrl: user?.avatarUrl || "",
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={saving}
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Save size={16} className="me-1" />
                    <span>{saving ? "Saving..." : "Save"}</span>
                  </Button>
                </div>
              )}
            </Card.Header>
            <Card.Body>
              {uploading && (
                <Alert
                  variant="info"
                  className="mb-3"
                  style={{ display: "flex", alignItems: "center", gap: 4 }}
                >
                  <Upload size={16} className="me-2" />
                  <span>Uploading file, please wait...</span>
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <h6>Email Address</h6>
                      <p>{user?.email || ""}</p>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <h6>Full Name</h6>
                      <Form.Control
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        readOnly={!isEditing}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <h6>Phone Number</h6>
                      <Form.Control
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        readOnly={!isEditing}
                        placeholder="Enter phone number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}></Col>
                </Row>

                {/* Additional Info Section */}
                <div className="mt-4 pt-3 border-top">
                  <h6 className="mb-3">Additional Information</h6>
                  <Row>
                    {user?.batches && user.batches.length > 0 && (
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Batches</Form.Label>
                          <div>
                            {user.batches.map((batch, index) => (
                              <Badge
                                key={index}
                                bg="info"
                                className="me-1 mb-1"
                              >
                                {batch.name}
                              </Badge>
                            ))}
                          </div>
                        </Form.Group>
                      </Col>
                    )}

                    {user?.createdAt && <Col md={6}></Col>}
                  </Row>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
